import React, { useState } from 'react';
import { clsx } from 'clsx';

export interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'boxed' | 'bordered' | 'lifted';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange,
  variant,
  size = 'md',
  className,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id || '');

  const activeTab = controlledActiveTab ?? internalActiveTab;

  const handleTabClick = (tabId: string) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  };

  const tabClasses = clsx(
    'tab',
    variant === 'boxed' && 'tab-boxed',
    variant === 'bordered' && 'tab-bordered',
    variant === 'lifted' && 'tab-lifted',
    size === 'xs' && 'tab-xs',
    size === 'sm' && 'tab-sm',
    size === 'lg' && 'tab-lg'
  );

  const activeTabData = tabs.find((tab) => tab.id === activeTab);

  return (
    <div className={className}>
      <div className={clsx('tabs', variant === 'boxed' && 'tabs-boxed')} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            className={clsx(
              tabClasses,
              activeTab === tab.id && 'tab-active',
              tab.disabled && 'tab-disabled'
            )}
            onClick={() => !tab.disabled && handleTabClick(tab.id)}
            disabled={tab.disabled}
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTabData && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeTabData.id}`}
          className={clsx(
            'pt-4',
            variant === 'bordered' && 'border border-t-0 border-base-300 p-4 rounded-b-lg',
            variant === 'lifted' && 'border border-t-0 border-base-300 p-4 rounded-b-lg'
          )}
        >
          {activeTabData.content}
        </div>
      )}
    </div>
  );
};
